import { clientBrainActionSchema, clientBrainProposalSchema } from "../clientBrainActions/models";
import { z } from "zod";
import { dataScopeSchema, workflowStatisticsQuerySchema, workflowStatisticsResultSchema, workflowDataQuerySchema } from "../workflowData/models";
import type { DataScope } from "../workflowData/models";
import { workflowContextSelectorSchema } from "../../components/NoteWorkflows/noteWorkflowsSchema";

export const builderModeSchema = z.enum(["build", "preview"]);
export type BuilderMode = z.infer<typeof builderModeSchema>;
export const reminderScheduleSchema = z.object({
    frequency: z.enum(["once", "daily", "weekly"]),
    time: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/),
    timezone: z.string().min(1).refine(value => {
        try { new Intl.DateTimeFormat("en", { timeZone: value }); return true; } catch { return false; }
    }, "Invalid time zone"),
    date: z.string().nullable(),
    weekdays: z.array(z.number().int().min(1).max(7)).max(7)
}).superRefine((schedule, context) => {
    if (schedule.frequency === "once" && !/^\d{4}-\d{2}-\d{2}$/.test(schedule.date ?? "")) context.addIssue({ code: "custom", path: ["date"], message: "Date is required" });
    if (schedule.frequency === "weekly" && schedule.weekdays.length === 0) context.addIssue({ code: "custom", path: ["weekdays"], message: "Select at least one weekday" });
    if (new Set(schedule.weekdays).size !== schedule.weekdays.length) context.addIssue({ code: "custom", path: ["weekdays"], message: "Weekdays must be unique" });
});
export const projectSummaryConfigSchema = z.object({
    data_query: workflowDataQuerySchema.nullish(),
    data_scope: dataScopeSchema.nullish(),
    analytics: workflowStatisticsQuerySchema.nullish(),
    output_format: z.enum(["card", "docx"]).optional(),
    report_template: z.string().max(20000).optional(),
    report_mode: z.enum(["auto", "text_only"]).optional(),
    project_id: z.string().min(1).max(200).regex(/^\S+$/).nullable(),
    project_name: z.string().max(200),
    context_selectors: z.array(workflowContextSelectorSchema).min(1).max(5).refine(values => new Set(values).size === values.length),
    instructions: z.string().min(1).max(12000).refine(value => value.trim().length > 0),
    note_limit: z.number().int().min(1).max(1000).nullable().optional(),
    note_workflow_title: z.string().trim().min(1).max(200).refine(
        // The backend ignores these leading icon decorations when matching labels.
        value => !/^[\p{White_Space}\p{So}\u001C-\u001F\uFE0E\uFE0F\u200D\u{1F3FB}-\u{1F3FF}]*$/u.test(value),
        "Note workflow types must include a name"
    ).nullable().optional(),
    language: z.string().min(2).max(20)
}).superRefine((summary, context) => {
    if (summary.analytics && !summary.context_selectors.includes("notes.signed")) context.addIssue({ code: "custom", path: ["analytics"], message: "Statistics require Note History" });
    if (summary.output_format !== "docx" && summary.report_template) context.addIssue({ code: "custom", path: ["report_template"], message: "Templates require Word report output" });
    if (summary.output_format !== "docx" && summary.report_mode === "text_only") context.addIssue({ code: "custom", path: ["report_mode"], message: "Text-only mode requires Word report output" });
    if (!summary.context_selectors.includes("notes.signed")) {
        if (summary.note_limit != null) context.addIssue({ code: "custom", path: ["note_limit"], message: "Note limits require Note History as a source" });
        if (summary.note_workflow_title != null) context.addIssue({ code: "custom", path: ["note_workflow_title"], message: "Note workflow types require Note History as a source" });
    }
});
export const noteMonitorConfigSchema = z.object({
    project_id: z.string().min(1).max(200).regex(/^\S+$/).nullable(),
    project_name: z.string().max(200),
    note_workflow_title: z.string().trim().min(1).max(200),
    content_condition: z.string().trim().min(1).max(4000).nullable().optional(),
    scope: z.enum(["all_history", "since_activation"]),
    threshold: z.number().int().min(1).max(1000000),
    language: z.string().min(2).max(20)
});
export type NoteMonitorConfig = z.infer<typeof noteMonitorConfigSchema>;
export const monitorStateSchema = z.object({
    activated_at: z.number().finite()
});
export const reminderConfigSchema = z.object({
    message: z.string().min(1),
    schedule: reminderScheduleSchema.nullable().default(null),
    project_summary: projectSummaryConfigSchema.nullable().optional(),
    note_monitor: noteMonitorConfigSchema.nullable().optional()
}).superRefine((value, context) => {
    if (Boolean(value.schedule) === Boolean(value.note_monitor) || (value.note_monitor && value.project_summary)) {
        context.addIssue({ code: "custom", message: "Choose either a schedule or a Note count monitor" });
    }
});
export const reminderStateSchema = z.object({
    enabled: z.boolean(), completed: z.boolean().default(false), workflow_revision: z.number().int(), next_run_at: z.string().nullable(),
    last_run_at: z.string().nullable(), saved_at: z.string(),
    last_error: z.object({ code: z.string(), message: z.string() }).nullable().optional()
});
export const workflowBoxSchema = z.object({
    client_brain_action: clientBrainActionSchema.nullish(),
    analytics: workflowStatisticsQuerySchema.nullish(),
    id: z.string(),
    title: z.string(),
    instructions: z.string(),
    context_selectors: z.array(workflowContextSelectorSchema).max(5).refine(values => new Set(values).size === values.length).default([]),
    output_type: z.enum(["text", "select"]),
    options: z.array(z.object({ value: z.string(), label: z.string() }))
});
export const builderWorkflowSchema = z.object({
    timezone: z.string().optional(),
    data_query: workflowDataQuerySchema.nullish(),
    data_scope: dataScopeSchema.nullish(),
    kind: z.enum(["document", "reminder"]).default("document"),
    reminder: reminderConfigSchema.nullable().default(null),
    title: z.string(),
    description: z.string(),
    boxes: z.array(workflowBoxSchema)
}).superRefine((workflow, context) => {
    workflow.boxes.forEach((box, index) => {
        if (box.analytics && (!box.context_selectors.includes("notes.signed") || box.output_type !== "text")) context.addIssue({ code: "custom", path: ["boxes", index, "analytics"], message: "Statistics require Note History and a text result" });
    });
    if (workflow.kind === "reminder" && (!workflow.reminder || workflow.boxes.length > 0)) context.addIssue({ code: "custom", message: "Reminder workflows require a reminder and no document boxes" });
    if (workflow.kind === "document" && workflow.reminder !== null) context.addIssue({ code: "custom", message: "Document workflows cannot contain a reminder" });
});
export const clientBrainSourceSchema = z.object({
    selector: z.literal("client.brain"),
    project_id: z.string().optional(),
    source_type: z.literal("client_brain"),
    source_id: z.string(),
    source_version: z.number(),
    source_path: z.string(),
    source_label: z.string(),
    source_excerpt: z.string(),
    source_entry_kind: z.enum(["field", "goal", "risk"])
});
export const signedNoteSourceSchema = z.object({
    selector: z.literal("notes.signed"),
    project_id: z.string().optional(),
    source_type: z.literal("signed_note"),
    source_id: z.string(),
    source_version: z.number(),
    source_path: z.literal("content"),
    source_label: z.string(),
    source_excerpt: z.string(),
    source_entry_kind: z.literal("note")
});
export const workflowDataSourceSchema = z.object({
    selector: z.enum(["workspace.tasks", "planning.schedule", "organization.structure"]), source_type: z.literal("workflow_data"),
    source_id: z.string(), source_version: z.number().int().min(1), source_path: z.literal("record"), source_label: z.string(), source_excerpt: z.string(), source_entry_kind: z.literal("record")
});
export const workflowSourceReferenceSchema = z.discriminatedUnion("source_type", [clientBrainSourceSchema, signedNoteSourceSchema, workflowDataSourceSchema]);
export const workflowDraftSchema = z.object({
    id: z.string(),
    title: z.string(),
    revision: z.number().int(),
    workflow_revision: z.number().int(),
    workflow: builderWorkflowSchema,
    schedule_state: reminderStateSchema.nullable().default(null),
    monitor_state: monitorStateSchema.nullable().optional(),
    messages: z.array(z.object({
        id: z.string(), role: z.enum(["user", "assistant"]), content: z.string(),
        intent: z.enum(["build", "preview", "clarify"]), created_at: z.string(),
        data_scope: dataScopeSchema.nullish(),
        project_id: z.string().nullable().default(null)
    })),
    preview: z.object({
        workflow_revision: z.number().int(), input: z.string(), created_at: z.string(),
        data_scope: dataScopeSchema.nullish(),
        project_id: z.string().nullable().default(null),
        project_name: z.string().nullable().default(null),
        client_brain_version: z.number().nullable().default(null),
        boxes: z.array(z.object({
            client_brain_proposal: clientBrainProposalSchema.nullish(),
            box_id: z.string(), content: z.string(), selected_value: z.string().nullable(),
            statistics: workflowStatisticsResultSchema.nullish(),
            evidence_spans: z.array(z.string().min(1).max(16000)).max(20).default([]),
            source_references: z.array(workflowSourceReferenceSchema).default([])
        }))
    }).nullable(),
    created_at: z.string(),
    updated_at: z.string()
});
export const draftSummarySchema = z.object({
    id: z.string(), title: z.string(), description: z.string(), revision: z.number().int(), updated_at: z.string(), box_count: z.number().int(),
    kind: z.enum(["document", "reminder"]).default("document"),
    reminder: reminderConfigSchema.nullable().default(null),
    workflow_revision: z.number().int().default(0),
    schedule_state: reminderStateSchema.nullable().default(null)
});
export type ReminderConfig = z.infer<typeof reminderConfigSchema>;
export type ProjectSummaryConfig = z.infer<typeof projectSummaryConfigSchema>;
export function reportModeForEdit(original: ProjectSummaryConfig, edited: Pick<ProjectSummaryConfig, "output_format" | "instructions" | "report_template">): "auto" | "text_only" {
    // A manually changed request may now require analytics. Only a new Build
    // interpretation can mark that changed report as narrative-only again.
    if (edited.output_format !== "docx" || edited.instructions.trim() !== original.instructions.trim() ||
        (edited.report_template ?? "").trim() !== (original.report_template ?? "").trim()) return "auto";
    return original.report_mode ?? "auto";
}
export type ReminderSchedule = z.infer<typeof reminderScheduleSchema>;
export type ReminderState = z.infer<typeof reminderStateSchema>;
export type WorkflowBox = z.infer<typeof workflowBoxSchema>;
export type BuilderWorkflow = z.infer<typeof builderWorkflowSchema>;
export type WorkflowDraft = z.infer<typeof workflowDraftSchema>;
export type DraftSummary = z.infer<typeof draftSummarySchema>;
export type EditReminderRequest = {
    request_id: string; expected_revision: number; title: string; description: string; reminder: ReminderConfig;
};
export type SetReminderEnabledRequest = { request_id: string; expected_revision: number; enabled: boolean };
export type EditWorkflowNodeRequest = {
    request_id: string;
    expected_revision: number;
    title: string;
    description: string;
} & ({ node_kind: "trigger"; box_id: null } | { node_kind: "step"; box_id: string });
export type ChatRequest = {
    message: string;
    mode: BuilderMode;
    request_id: string;
    language: string;
    timezone?: string;
    expected_revision?: number;
    preview_selections?: Record<string, string>;
    project_id?: string | null;
    data_scope?: DataScope | null;
};
